export type Ok<T> = { ok: true; data: T };
export type Err<E = Error> = { ok: false; error: E };
export type Result<T, E = Error> = Ok<T> | Err<E>;

export function toError(e: unknown): Error {
  if (e instanceof Error) return e;
  return new Error(typeof e === 'string' ? e : JSON.stringify(e));
}

/**
 * ✅ Sync version
 * Wraps a synchronous function call safely.
 */
export function withSafeResultSync<T, E = Error>(
  fn: () => T,
  mapError?: (e: unknown) => E,
): Result<T, E> {
  try {
    const data = fn();
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: (mapError ? mapError(err) : toError(err)) as E };
  }
}

/**
 * ✅ Async version
 * Wraps a Promise or async function safely.
 */
export async function withSafeResultAsync<T, E = Error>(
  promiseOrFn: Promise<T> | (() => Promise<T>),
  mapError?: (e: unknown) => E,
): Promise<Result<T, E>> {
  try {
    const promise = typeof promiseOrFn === 'function' ? promiseOrFn() : promiseOrFn;
    const data = await promise;
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: (mapError ? mapError(err) : toError(err)) as E };
  }
}
